import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { teamsApi, Team, TeamMember } from '../api/teams';
import { useAuth } from '../contexts/AuthContext';
import TemplateBar from '../components/TemplateBar';
import Tooltip from '../components/Tooltip';
import { useTranslation } from 'react-i18next';

function roleLabel(role: TeamMember['role'], t: (k: string) => string) {
  if (role === 'captain') return t('team.roleCapt');
  if (role === 'co_captain') return t('team.roleCoCapt');
  return t('team.rolePlayer');
}

function TeamCard({ team }: { team: Team }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editingRanking, setEditingRanking] = useState(false);
  const [rankingEdit, setRankingEdit] = useState<Record<string, number>>({});
  const [editingInfo, setEditingInfo] = useState(false);
  const [editName, setEditName] = useState(team.name);
  const [editLocation, setEditLocation] = useState(team.home_location ?? '');
  const [editError, setEditError] = useState('');

  const { data: members = [] } = useQuery({
    queryKey: ['team-members', team.id],
    queryFn: () => teamsApi.getMembers(team.id).then((r) => r.data),
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) => teamsApi.inviteMember(team.id, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', team.id] });
      setInviteEmail('');
      setShowInviteForm(false);
      setInviteError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setInviteError(e.response?.data?.detail ?? t('team.createError'));
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => teamsApi.removeMember(team.id, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['team-members', team.id] }),
  });

  const saveRankingMutation = useMutation({
    mutationFn: (rankings: { user_id: string; ranking_position: number }[]) =>
      teamsApi.updateRanking(team.id, rankings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-members', team.id] });
      setEditingRanking(false);
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: () => teamsApi.update(team.id, { name: editName, home_location: editLocation || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-teams'] });
      setEditingInfo(false);
      setEditError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setEditError(e.response?.data?.detail ?? t('team.createError'));
    },
  });

  const isCaptain = user && team.captain_id === user.id;
  const activeMembers = members.filter((m) => m.is_active && m.invite_status === 'accepted');
  const pendingMembers = members.filter((m) => m.invite_status === 'pending');

  function startRankingEdit() {
    const initial: Record<string, number> = {};
    activeMembers.forEach((m) => { initial[m.user_id] = m.ranking_position ?? 0; });
    setRankingEdit(initial);
    setEditingRanking(true);
  }

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="card">
        {editingInfo ? (
          <div className="space-y-3">
            {editError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{editError}</div>
            )}
            <div>
              <label className="label">{t('team.editName')}</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="label">{t('team.editLocation')}</label>
              <input
                type="text"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                className="input"
                placeholder="z.B. Padel Center Nord"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setEditingInfo(false)} className="btn btn-secondary btn-sm">{t('team.cancel')}</button>
              <button
                onClick={() => updateTeamMutation.mutate()}
                disabled={updateTeamMutation.isPending || !editName.trim()}
                className="btn btn-primary btn-sm"
              >
                {updateTeamMutation.isPending ? t('team.saving') : t('team.saveChanges')}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{team.name}</h2>
              {team.home_location && (
                <p className="text-sm text-gray-500 mt-1">📍 {team.home_location}</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="badge border border-orange-300 bg-orange-50 text-orange-700">{t('team.badge')}</span>
              {isCaptain && (
                <button onClick={() => setEditingInfo(true)} className="btn btn-secondary btn-sm">
                  {t('team.editTeam')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pending invitations */}
      {isCaptain && pendingMembers.length > 0 && (
        <div className="card border border-yellow-200 bg-yellow-50">
          <h3 className="text-sm font-semibold text-yellow-800 mb-3">
            {t('team.pendingTitle', { count: pendingMembers.length })}
          </h3>
          <div className="space-y-2">
            {pendingMembers.map((member) => (
              <div key={member.user_id} className="flex items-center justify-between">
                <span className="text-sm text-yellow-900">
                  {member.user ? <Link to={`/users/${member.user_id}`} className="hover:text-primary-600">{member.user.first_name} {member.user.last_name}</Link> : member.user_id}
                </span>
                <button
                  onClick={() => removeMutation.mutate(member.user_id)}
                  disabled={removeMutation.isPending}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  {t('team.revokeInvite')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Squad */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">
            {t('team.squadTitle', { count: activeMembers.length })}
          </h3>
          {isCaptain && !editingRanking && (
            <div className="flex gap-2">
              <button onClick={startRankingEdit} className="btn btn-secondary btn-sm">
                {t('team.editRanking')}
              </button>
              <button onClick={() => setShowInviteForm(!showInviteForm)} className="btn btn-primary btn-sm">
                {t('team.invitePlayer')}
              </button>
            </div>
          )}
          {isCaptain && editingRanking && (
            <div className="flex gap-2">
              <button
                onClick={() => saveRankingMutation.mutate(
                  Object.entries(rankingEdit).map(([uid, pos]) => ({ user_id: uid, ranking_position: pos }))
                )}
                disabled={saveRankingMutation.isPending}
                className="btn btn-primary btn-sm"
              >
                {t('team.saveRanking')}
              </button>
              <button onClick={() => setEditingRanking(false)} className="btn btn-secondary btn-sm">
                {t('team.abortRanking')}
              </button>
            </div>
          )}
        </div>

        {showInviteForm && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            {inviteError && <div className="text-red-600 text-sm mb-2">{inviteError}</div>}
            <div className="flex gap-2">
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('team.inviteEmail')}
                className="input flex-1 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && inviteEmail && inviteMutation.mutate(inviteEmail)}
              />
              <button
                onClick={() => inviteMutation.mutate(inviteEmail)}
                disabled={inviteMutation.isPending || !inviteEmail}
                className="btn btn-primary btn-sm"
              >
                {inviteMutation.isPending ? '...' : t('team.invite')}
              </button>
              <button onClick={() => { setShowInviteForm(false); setInviteError(''); }} className="btn btn-secondary btn-sm">
                {t('team.cancel')}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-1">
          {activeMembers.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">{t('team.noMembers')}</p>
          )}
          {activeMembers.map((member, idx) => (
            <div key={member.user_id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-3">
                <span className="text-xs text-gray-400 w-8 text-center font-mono">
                  {editingRanking ? (
                    <input
                      type="number"
                      min="1"
                      value={rankingEdit[member.user_id] ?? idx + 1}
                      onChange={(e) =>
                        setRankingEdit((prev) => ({ ...prev, [member.user_id]: parseInt(e.target.value) || 0 }))
                      }
                      className="w-10 border border-gray-300 rounded px-1 py-0 text-xs"
                    />
                  ) : (
                    member.ranking_position ?? '–'
                  )}
                </span>
                <div>
                  {member.user
                    ? <Link to={`/users/${member.user_id}`} className="font-medium text-gray-900 text-sm hover:text-primary-600">{member.user.first_name} {member.user.last_name}</Link>
                    : <span className="font-medium text-gray-900 text-sm">{member.user_id}</span>}
                  {member.user?.level && (
                    <span className="ml-2 text-xs text-gray-400">{member.user.level}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`badge text-xs ${
                  member.role === 'captain' ? 'bg-orange-100 text-orange-700' :
                  member.role === 'co_captain' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-500'
                }`}>
                  {roleLabel(member.role, t)}
                </span>
                {isCaptain && member.role !== 'captain' && !editingRanking && (
                  <button
                    onClick={() => removeMutation.mutate(member.user_id)}
                    disabled={removeMutation.isPending}
                    className="text-xs text-red-500 hover:text-red-700"
                  >
                    {t('team.removePlayer')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TeamManagement() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [createError, setCreateError] = useState('');

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ['my-teams'],
    queryFn: () => teamsApi.listMine().then((r) => r.data),
    enabled: !!user,
  });

  const createMutation = useMutation({
    mutationFn: () => teamsApi.create({ name: newName, home_location: newLocation || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-teams'] });
      setNewName('');
      setNewLocation('');
      setShowCreateForm(false);
      setCreateError('');
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      setCreateError(e.response?.data?.detail ?? t('team.createError'));
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        <div className="animate-pulse h-8 bg-gray-200 rounded w-1/3" />
        <div className="animate-pulse h-48 bg-gray-100 rounded" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('team.pageTitle')}</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary btn-sm"
        >
          {t('team.createNew')}
        </button>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="card mb-6 border border-primary-200">
          <h2 className="font-semibold text-gray-900 mb-4">{t('team.createNew')}</h2>
          <TemplateBar
            templateType="team"
            currentData={{ name: newName, home_location: newLocation }}
            onApply={(data) => {
              setNewName((data.name as string) ?? '');
              setNewLocation((data.home_location as string) ?? '');
            }}
          />
          {createError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">{createError}</div>
          )}
          <div className="space-y-3">
            <div>
              <label className="label">
                {t('team.teamName')}
                <Tooltip text={t('tooltips.teamName')} />
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="input"
                placeholder="z.B. Smash Brothers"
                autoFocus
              />
            </div>
            <div>
              <label className="label">
                {t('team.homeLocation')}
                <Tooltip text={t('tooltips.teamHomeLocation')} />
              </label>
              <input
                type="text"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                className="input"
                placeholder="z.B. Padel Center Nord"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => { setShowCreateForm(false); setCreateError(''); }} className="btn btn-secondary">
              {t('team.cancel')}
            </button>
            <button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !newName.trim()}
              className="btn btn-primary"
            >
              {createMutation.isPending ? t('team.creating') : t('team.create')}
            </button>
          </div>
        </div>
      )}

      {/* Teams list */}
      {teams.length === 0 && !showCreateForm ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 mb-2">{t('team.noTeam')}</p>
          <p className="text-sm text-gray-400 mb-6">{t('team.createHint')}</p>
          <button onClick={() => setShowCreateForm(true)} className="btn btn-primary">
            {t('team.createNew')}
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {(teams as Team[]).map((team) => (
            <TeamCard key={team.id} team={team} />
          ))}
        </div>
      )}
    </div>
  );
}
